import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5005';

function isValidProfileName(name) {
  return /^[a-zA-Z0-9_-]+$/.test(name);
}

function LoginPage({ onLogin }) {
  const [userName, setUserName] = useState('');

  const handleLogin = (e) => {
    e.preventDefault();
    if (userName.trim()) {
      localStorage.setItem('userName', userName.trim());
      onLogin(userName.trim());
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>Data Collection</h1>
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="userName">Enter Your Name:</label>
            <input
              id="userName"
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="Enter your name"
              className="input-field"
              autoFocus
            />
          </div>
          <button type="submit" className="btn btn-login" disabled={!userName.trim()}>
            Login
          </button>
        </form>
      </div>
    </div>
  );
}

function MainPage({ userName, onLogout }) {
  const [products, setProducts] = useState([]);
  const [activeProduct, setActiveProduct] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedProducts, setRecordedProducts] = useState(new Set());
  const [recordingError, setRecordingError] = useState(null);
  const [playingAudio, setPlayingAudio] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRefs = useRef({});

  // Upload panel state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStats, setUploadStats] = useState(null);
  const fileInputRef = useRef(null);

  // Download zip state
  const [dlStatus, setDlStatus] = useState(null);
  const [dlPercent, setDlPercent] = useState(0);
  const [downloadUser, setDownloadUser] = useState('all');
  const dlPollRef = useRef(null);

  // Profile state — activeProfile starts null until profiles are fetched
  const [activeProfile, setActiveProfile] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [newProfileName, setNewProfileName] = useState('');
  const [profileCreateStatus, setProfileCreateStatus] = useState(null);

  // Load profile list once on mount; pick active profile from saved value or first in list
  useEffect(() => {
    axios.get(`${API_BASE_URL}/profiles`)
      .then(res => {
        if (res.data.success) {
          const list = res.data.profiles.sort();
          setProfiles(list);
          if (list.length > 0) {
            const saved = localStorage.getItem('activeProfile');
            const initial = (saved && list.includes(saved)) ? saved : list[0];
            setActiveProfile(initial);
            localStorage.setItem('activeProfile', initial);
          }
        }
      })
      .catch(() => {});
  }, []);

  const loadProducts = (profile) => {
    axios.get(`${API_BASE_URL}/products?profile=${encodeURIComponent(profile)}`)
      .then(response => {
        if (response.data.success) setProducts(response.data.products);
        else console.error('Error loading products:', response.data.error);
      })
      .catch(err => console.error('Error loading products:', err));
  };

  const refreshStats = (profile) => {
    axios.get(`${API_BASE_URL}/upload-stats?profile=${encodeURIComponent(profile)}`)
      .then(res => { if (res.data.success) setUploadStats(res.data); })
      .catch(() => {});
  };

  // Reload data whenever the active profile changes; also drives the stats interval
  useEffect(() => {
    if (!activeProfile) return;
    setProducts([]);
    setRecordedProducts(new Set());
    setUploadStats(null);
    setActiveProduct(null);
    setDownloadUser('all');
    loadProducts(activeProfile);
    refreshStats(activeProfile);
    const statsInterval = setInterval(() => refreshStats(activeProfile), 30000);
    return () => {
      clearInterval(statsInterval);
      if (dlPollRef.current) clearInterval(dlPollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfile]);

  const checkRecordedProducts = async (productsList) => {
    try {
      const results = await Promise.all(
        productsList.map(product =>
          axios.get(`${API_BASE_URL}/check-audio`, {
            params: { productName: product.Bangla_products, userName, profile: activeProfile }
          })
            .then(res => ({ sku: product.SKU, exists: res.data.success && res.data.exists }))
            .catch(() => ({ sku: product.SKU, exists: false }))
        )
      );
      setRecordedProducts(new Set(results.filter(r => r.exists).map(r => r.sku)));
    } catch (err) {
      console.error('Error checking recorded products:', err);
    }
  };

  useEffect(() => {
    if (products.length > 0 && userName) checkRecordedProducts(products);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, userName]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && products.length > 0 && userName) {
        checkRecordedProducts(products);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, userName]);

  // Force a real reload when the page is restored from bfcache (browser
  // back/forward), otherwise the app keeps showing whatever was in memory
  // from before the last upload instead of refetching current data.
  useEffect(() => {
    const handlePageShow = (e) => {
      if (e.persisted) window.location.reload();
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // ---------------------------------------------------------------------------
  // Profile management
  // ---------------------------------------------------------------------------

  const handleSwitchProfile = (newProfile) => {
    localStorage.setItem('activeProfile', newProfile);
    setDownloadUser('all');
    setActiveProfile(newProfile);
    // Data reset + reload is handled by the activeProfile useEffect
  };

  const handleCreateProfile = async () => {
    const name = newProfileName.trim();
    if (!name || !isValidProfileName(name)) {
      setProfileCreateStatus({ type: 'error', message: 'Use letters, numbers, _ or - only.' });
      setTimeout(() => setProfileCreateStatus(null), 3000);
      return;
    }
    try {
      setProfileCreateStatus({ type: 'loading', message: 'Creating...' });
      const res = await axios.post(`${API_BASE_URL}/create-profile`, { name });
      if (res.data.success) {
        const created = res.data.profile;
        setProfiles(prev => [...prev, created].sort());
        setNewProfileName('');
        setProfileCreateStatus({ type: 'success', message: `Profile '${created}' created` });
        handleSwitchProfile(created);
        setTimeout(() => setProfileCreateStatus(null), 3000);
      } else {
        setProfileCreateStatus({ type: 'error', message: res.data.error });
        setTimeout(() => setProfileCreateStatus(null), 4000);
      }
    } catch (err) {
      setProfileCreateStatus({ type: 'error', message: err.response?.data?.error || err.message });
      setTimeout(() => setProfileCreateStatus(null), 4000);
    }
  };

  // ---------------------------------------------------------------------------
  // Recording
  // ---------------------------------------------------------------------------

  const handleProductClick = (product) => {
    if (isRecording) return;
    if (recordedProducts.has(product.SKU)) return;
    setActiveProduct(prev => (prev === product.SKU ? null : product.SKU));
    axios.get(`${API_BASE_URL}/check-audio`, {
      params: { productName: product.Bangla_products, userName, profile: activeProfile }
    }).then(response => {
      if (response.data.success && response.data.exists) {
        setRecordedProducts(prev => new Set([...prev, product.SKU]));
        setActiveProduct(null);
      }
    }).catch(() => {});
  };

  const startRecording = async (product) => {
    if (!product || !userName) return;
    setRecordingError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });

      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/mp4';
          if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = '';
        }
      }

      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        try {
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });
          await convertAndSaveAudio(audioBlob, product);
        } catch (err) {
          console.error('Error processing audio:', err);
        } finally {
          stream.getTracks().forEach(t => t.stop());
        }
      };

      mediaRecorder.start(100);
      setIsRecording(true);
    } catch (err) {
      console.error('Error starting recording:', err);
      setRecordingError('Microphone access denied or unavailable.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleRecordClick = (product) => {
    if (isRecording) stopRecording();
    else startRecording(product);
  };

  const convertAndSaveAudio = async (audioBlob, product) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      const targetSampleRate = 16000;
      const length = Math.round(audioBuffer.length * targetSampleRate / audioBuffer.sampleRate);
      const offlineContext = new OfflineAudioContext(1, length, targetSampleRate);
      const source = offlineContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(offlineContext.destination);
      source.start(0);
      const resampledBuffer = await offlineContext.startRendering();
      const wavBlob = audioBufferToWav(resampledBuffer);
      audioContext.close();

      if (!wavBlob || wavBlob.size === 0) throw new Error('Failed to create WAV file');

      const sanitizedUserName = userName.replace(/[^a-zA-Z0-9_-]/g, '_');
      const fileName = `${sanitizedUserName}_${product.SKU}_${Date.now()}.wav`;
      const formData = new FormData();
      formData.append('audio', wavBlob, fileName);
      formData.append('userName', userName);
      formData.append('productName', product.Bangla_products);
      formData.append('profile', activeProfile);

      const response = await axios.post(`${API_BASE_URL}/save-audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        setRecordedProducts(prev => new Set([...prev, product.SKU]));
        refreshStats(activeProfile);
        setTimeout(() => setActiveProduct(null), 500);
      } else {
        console.error('Error saving audio:', response.data.error);
      }
    } catch (err) {
      console.error('Error processing audio:', err);
      setRecordingError('Failed to save recording. Please try again.');
    }
  };

  const audioBufferToWav = (buffer) => {
    const length = buffer.length;
    const numberOfChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const bytesPerSample = 2;
    const blockAlign = numberOfChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataSize = length * blockAlign;
    const fileSize = 36 + dataSize;

    const ab = new ArrayBuffer(44 + dataSize);
    const view = new DataView(ab);
    let offset = 0;

    const writeString = (o, s) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };

    writeString(offset, 'RIFF'); offset += 4;
    view.setUint32(offset, fileSize, true); offset += 4;
    writeString(offset, 'WAVE'); offset += 4;
    writeString(offset, 'fmt '); offset += 4;
    view.setUint32(offset, 16, true); offset += 4;
    view.setUint16(offset, 1, true); offset += 2;
    view.setUint16(offset, numberOfChannels, true); offset += 2;
    view.setUint32(offset, sampleRate, true); offset += 4;
    view.setUint32(offset, byteRate, true); offset += 4;
    view.setUint16(offset, blockAlign, true); offset += 2;
    view.setUint16(offset, 16, true); offset += 2;
    writeString(offset, 'data'); offset += 4;
    view.setUint32(offset, dataSize, true); offset += 4;

    const channels = [];
    for (let i = 0; i < numberOfChannels; i++) channels.push(buffer.getChannelData(i));
    for (let i = 0; i < length; i++) {
      for (let ch = 0; ch < numberOfChannels; ch++) {
        let sample = Math.max(-1, Math.min(1, channels[ch][i]));
        sample = Math.max(-32768, Math.min(32767, Math.round(sample * 32767)));
        view.setInt16(offset, sample, true);
        offset += 2;
      }
    }
    return new Blob([ab], { type: 'audio/wav' });
  };

  // ---------------------------------------------------------------------------
  // Playback
  // ---------------------------------------------------------------------------

  const handlePlayAudio = async (product) => {
    try {
      const checkResponse = await axios.get(`${API_BASE_URL}/check-audio`, {
        params: { productName: product.Bangla_products, userName, profile: activeProfile }
      });

      if (!checkResponse.data.success || !checkResponse.data.exists) {
        setRecordedProducts(prev => { const s = new Set(prev); s.delete(product.SKU); return s; });
        delete audioRefs.current[product.SKU];
        return;
      }

      if (playingAudio) {
        playingAudio.pause();
        playingAudio.currentTime = 0;
        if (playingAudio !== audioRefs.current[product.SKU]) setPlayingAudio(null);
      }

      const fileName = checkResponse.data.fileName;
      const audioUrl = `${API_BASE_URL}/audio/${encodeURIComponent(fileName)}?profile=${encodeURIComponent(activeProfile)}&userName=${encodeURIComponent(userName)}`;

      if (!audioRefs.current[product.SKU]) {
        const audio = new Audio(audioUrl);
        audioRefs.current[product.SKU] = audio;
        audio.onended = () => setPlayingAudio(null);
        audio.onerror = () => { setPlayingAudio(null); checkRecordedProducts([product]); };
      }

      const audio = audioRefs.current[product.SKU];
      audio.src = audioUrl;
      await audio.play();
      setPlayingAudio(audio);
    } catch (err) {
      console.error('Error playing audio:', err);
      checkRecordedProducts([product]);
    }
  };

  const handleStopAudio = () => {
    if (playingAudio) { playingAudio.pause(); playingAudio.currentTime = 0; setPlayingAudio(null); }
  };

  const handleDeleteAudio = async (product) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/delete-audio`, {
        data: { productName: product.Bangla_products, userName, profile: activeProfile }
      });
      if (response.data.success) {
        setRecordedProducts(prev => { const s = new Set(prev); s.delete(product.SKU); return s; });
        refreshStats(activeProfile);
        if (audioRefs.current[product.SKU]) {
          if (playingAudio === audioRefs.current[product.SKU]) handleStopAudio();
          delete audioRefs.current[product.SKU];
        }
      }
    } catch (err) {
      console.error('Error deleting audio:', err);
    }
  };

  // ---------------------------------------------------------------------------
  // Upload
  // ---------------------------------------------------------------------------

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) { setSelectedFile(file); setUploadStatus(null); }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setIsUploading(true);
    setUploadStatus(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await axios.post(
        `${API_BASE_URL}/upload-products?profile=${encodeURIComponent(activeProfile)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data.success) {
        setUploadStatus({ type: 'success', message: 'Uploaded successfully' });
        setUploadStats({ totalRows: res.data.totalRows, recordings: res.data.recordings });
        if (res.data.products) setProducts(res.data.products);
        else loadProducts(activeProfile);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        setUploadStatus({ type: 'error', message: res.data.error });
      }
    } catch (err) {
      setUploadStatus({ type: 'error', message: err.response?.data?.error || err.message });
    } finally {
      setIsUploading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Download
  // ---------------------------------------------------------------------------

  const handleDownload = async () => {
    if (dlStatus === 'zipping') return;
    setDlStatus('zipping');
    setDlPercent(0);
    try {
      const res = await axios.post(`${API_BASE_URL}/prepare-zip`, {
        profile: activeProfile,
        username: downloadUser === 'all' ? null : downloadUser,
      });
      if (!res.data.success) throw new Error(res.data.error);
      const jobId = res.data.jobId;

      dlPollRef.current = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API_BASE_URL}/zip-status/${jobId}`);
          const { status, percent, error } = statusRes.data;
          setDlPercent(percent);
          if (status === 'done') {
            clearInterval(dlPollRef.current);
            setDlPercent(100);
            setDlStatus('done');
            const a = document.createElement('a');
            a.href = `${API_BASE_URL}/download-zip/${jobId}`;
            a.download = 'recordings.zip';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => setDlStatus(null), 2000);
          } else if (status === 'error') {
            clearInterval(dlPollRef.current);
            setDlStatus('error');
            console.error('Zip error:', error);
            setTimeout(() => setDlStatus(null), 3000);
          }
        } catch {
          clearInterval(dlPollRef.current);
          setDlStatus('error');
          setTimeout(() => setDlStatus(null), 3000);
        }
      }, 400);
    } catch {
      setDlStatus('error');
      setTimeout(() => setDlStatus(null), 3000);
    }
  };

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------

  const isAdmin = userName.toLowerCase() === 'admin';
  const recordedCount = recordedProducts.size;
  const totalCount = products.length || (uploadStats?.totalRows ?? 0);
  const allRecordings = uploadStats?.recordings ?? [];

  const ownRecordings  = allRecordings.filter(r => r.username === userName);
  const lastThreeOwn   = ownRecordings.slice(0, 3);

  const userStatsMap  = allRecordings.reduce((acc, rec) => {
    acc[rec.username] = (acc[rec.username] || 0) + 1;
    return acc;
  }, {});
  const userStatsList = Object.entries(userStatsMap)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);

  const totalAllRecorded = allRecordings.length;
  const recordingUsers   = [...new Set(allRecordings.map(r => r.username))].sort();

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="main-container">
      <div className="main-header">
        <div className="header-top">
          <div className="header-brand">
            <svg className="header-logo" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z"/>
              <path d="M2 17l10 5 10-5"/>
              <path d="M2 12l10 5 10-5"/>
            </svg>
            <span className="header-title">Data Collection</span>
          </div>
          <div className="header-info">
            <span className="user-name">Welcome, {userName}</span>
            <button onClick={onLogout} className="btn btn-logout">Logout</button>
          </div>
        </div>
      </div>

      <div className="main-body">
        {/* Left panel */}
        <div className="left-panel">
          <div className={`left-panel-header ${isAdmin ? '' : 'left-panel-header-user'}`}>
            {isAdmin ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="left-panel-icon">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <path d="M3 9h18M9 21V9"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="left-panel-icon">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
            )}
            <span>{isAdmin ? 'Control Panel' : 'Session Stats'}</span>
            {isAdmin && <span className="admin-badge">Admin</span>}
          </div>

          <div className="left-panel-scroll">

            {/* Profile card — admin only */}
            {isAdmin && (
              <div className="profile-card">
                <div className="profile-card-header">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="profile-card-icon">
                    <path d="M3 7h18M3 12h18M3 17h18"/>
                    <circle cx="20" cy="7" r="0"/>
                  </svg>
                  <span className="profile-card-title">Profile</span>
                </div>

                {profiles.length > 0 ? (
                  <select
                    className="profile-select"
                    value={activeProfile || ''}
                    onChange={(e) => handleSwitchProfile(e.target.value)}
                  >
                    {profiles.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                ) : (
                  <div className="profile-empty-msg">No profiles yet. Create one below.</div>
                )}

                <div className="profile-create-row">
                  <input
                    type="text"
                    className="profile-name-input"
                    value={newProfileName}
                    onChange={(e) => setNewProfileName(e.target.value)}
                    placeholder="New profile name"
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateProfile()}
                  />
                  <button
                    className="btn-create-profile"
                    onClick={handleCreateProfile}
                    disabled={!newProfileName.trim()}
                  >
                    Create
                  </button>
                </div>

                {profileCreateStatus && (
                  <div className={`profile-status ${profileCreateStatus.type}`}>
                    {profileCreateStatus.message}
                  </div>
                )}
              </div>
            )}

            {/* Upload card — admin only */}
            {isAdmin && (
              <div className="upload-card">
                <div className="upload-card-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="upload-card-icon">
                    <polyline points="16 16 12 12 8 16"/>
                    <line x1="12" y1="12" x2="12" y2="21"/>
                    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                  </svg>
                  <div>
                    <div className="upload-card-title">Upload Products</div>
                    <div className="upload-card-subtitle">CSV or Excel (.xlsx / .xls)</div>
                  </div>
                </div>

                <div
                  className={`file-drop-area ${selectedFile ? 'has-file' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  {selectedFile ? (
                    <div className="file-chosen-content">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="file-icon">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                      </svg>
                      <span className="file-chosen">{selectedFile.name}</span>
                    </div>
                  ) : (
                    <div className="file-placeholder-content">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="file-placeholder-icon">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                      <span className="file-placeholder">Click to choose file</span>
                      <span className="file-hint">CSV, XLSX, XLS supported</span>
                    </div>
                  )}
                </div>

                <button
                  className="btn btn-upload"
                  onClick={handleUpload}
                  disabled={!selectedFile || isUploading}
                >
                  {isUploading ? <><span className="spinner"></span> Uploading...</> : 'Upload'}
                </button>

                {uploadStatus && (
                  <div className={`upload-status ${uploadStatus.type}`}>
                    {uploadStatus.type === 'success' ? '✓ ' : '✕ '}{uploadStatus.message}
                  </div>
                )}
              </div>
            )}

            {/* Stats card */}
            <div className="stats-card">
              {isAdmin ? (
                <>
                  <div className="stats-card-title">Global Overview</div>
                  <div className="stats-grid">
                    <div className="stat-box">
                      <span className="stat-number">{totalCount}</span>
                      <span className="stat-label">Products</span>
                    </div>
                    <div className="stat-box recorded-stat">
                      <span className="stat-number">{totalAllRecorded}</span>
                      <span className="stat-label">Recorded</span>
                    </div>
                    <div className="stat-box users-stat">
                      <span className="stat-number">{userStatsList.length}</span>
                      <span className="stat-label">Users</span>
                    </div>
                  </div>

                  {totalAllRecorded > 0 && totalCount > 0 && (
                    <div className="progress-bar-wrap">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${Math.min(100, Math.round((totalAllRecorded / totalCount) * 100))}%` }}
                      />
                    </div>
                  )}

                  {userStatsList.length > 0 && (
                    <div className="user-stats-section">
                      <div className="recorded-list-title">Users Progress</div>
                      {userStatsList.map((u) => (
                        <div key={u.name} className="user-stat-row">
                          <div className="user-stat-meta">
                            <span className="user-stat-name">{u.name}</span>
                            <span className="user-stat-count">{u.count}/{totalCount}</span>
                          </div>
                          <div className="user-mini-bar-bg">
                            <div
                              className="user-mini-bar-fill"
                              style={{ width: totalCount > 0 ? `${Math.min(100, Math.round((u.count / totalCount) * 100))}%` : '0%' }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="stats-card-title">My Progress</div>
                  <div className="stats-grid">
                    <div className="stat-box">
                      <span className="stat-number">{totalCount}</span>
                      <span className="stat-label">Total</span>
                    </div>
                    <div className="stat-box recorded-stat">
                      <span className="stat-number">{recordedCount}</span>
                      <span className="stat-label">Recorded</span>
                    </div>
                    <div className="stat-box pending-stat">
                      <span className="stat-number">{Math.max(0, totalCount - recordedCount)}</span>
                      <span className="stat-label">Remaining</span>
                    </div>
                  </div>

                  {recordedCount > 0 && (
                    <div className="progress-bar-wrap">
                      <div
                        className="progress-bar-fill"
                        style={{ width: totalCount > 0 ? `${Math.round((recordedCount / totalCount) * 100)}%` : '0%' }}
                      />
                    </div>
                  )}

                  {lastThreeOwn.length > 0 && (
                    <div className="recorded-list">
                      <h3 className="recorded-list-title">Recently Recorded</h3>
                      <div className="recorded-list-items">
                        {lastThreeOwn.map((rec, i) => (
                          <div key={i} className="recorded-list-item">
                            <span className="recorded-tick">✓</span>
                            <div className="recorded-info">
                              <span className="recorded-product">{rec.productname}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

          </div>{/* end left-panel-scroll */}

          {/* Download Dataset — admin only, sticky at bottom */}
          {isAdmin && (
            <div className="left-panel-footer">
              {dlStatus === 'zipping' ? (
                <div className="dl-progress-wrap">
                  <div className="dl-progress-label">
                    <span className="spinner dl-spinner"></span>
                    Zipping... {dlPercent}%
                  </div>
                  <div className="dl-progress-bar-bg">
                    <div className="dl-progress-bar-fill" style={{ width: `${dlPercent}%` }} />
                  </div>
                </div>
              ) : dlStatus === 'done' ? (
                <div className="dl-done-msg">✓ Download started!</div>
              ) : dlStatus === 'error' ? (
                <div className="dl-error-msg">✕ Zip failed. Try again.</div>
              ) : (
                <div className="dl-user-row">
                  <select
                    className="dl-user-select"
                    value={downloadUser}
                    onChange={(e) => setDownloadUser(e.target.value)}
                    disabled={!activeProfile}
                  >
                    <option value="all">All Users</option>
                    {recordingUsers.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                  <button
                    className="btn-download-zip"
                    onClick={handleDownload}
                    disabled={!activeProfile}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="dl-icon">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Download Dataset
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="panel-divider">
          <div className="panel-divider-line"></div>
        </div>

        {/* Right panel */}
        <div className="right-panel">
          <div className="right-panel-header">
            <div className="right-panel-title-group">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="right-panel-icon">
                <line x1="8" y1="6" x2="21" y2="6"/>
                <line x1="8" y1="12" x2="21" y2="12"/>
                <line x1="8" y1="18" x2="21" y2="18"/>
                <line x1="3" y1="6" x2="3.01" y2="6"/>
                <line x1="3" y1="12" x2="3.01" y2="12"/>
                <line x1="3" y1="18" x2="3.01" y2="18"/>
              </svg>
              <span className="right-panel-title">Products List</span>
            </div>
            <span className="products-count-badge">{products.length} items</span>
          </div>

          <div className="products-list">
            {products.length === 0 ? (
              <div className="no-products">Upload a product list to get started.</div>
            ) : (
              products.map((product, index) => {
                const isRecorded = recordedProducts.has(product.SKU);
                const isActive = activeProduct === product.SKU;
                const isPlaying = playingAudio === audioRefs.current[product.SKU];
                const isCurrentlyRecording = isRecording && activeProduct === product.SKU;

                return (
                  <div
                    key={product.SKU || index}
                    className={`product-item ${isActive ? 'active' : ''} ${isCurrentlyRecording ? 'recording' : ''}`}
                  >
                    <div className="product-details" onClick={() => handleProductClick(product)}>
                      <span className="product-name"><span className="product-index">{index + 1}.</span> {product.Bangla_products}</span>
                    </div>
                    <div className="product-actions">
                      {!isRecorded && (
                        <>
                          {isActive && (
                            <>
                              {isCurrentlyRecording && (
                                <span className="recording-status">
                                  <span className="pulse-dot"></span>
                                  Recording in progress...
                                </span>
                              )}
                              {recordingError && activeProduct === product.SKU && (
                                <span className="recording-error">{recordingError}</span>
                              )}
                              <button
                                className={`btn btn-record-item ${isRecording ? 'recording' : ''}`}
                                onClick={() => handleRecordClick(product)}
                                disabled={isRecording && activeProduct !== product.SKU}
                              >
                                {isRecording ? 'Stop Recording' : 'Record'}
                              </button>
                            </>
                          )}
                        </>
                      )}
                      {isRecorded && (
                        <>
                          <span className="tick-sign">✓</span>
                          <button
                            className="btn btn-play"
                            onClick={() => isPlaying ? handleStopAudio() : handlePlayAudio(product)}
                          >
                            {isPlaying ? 'Stop' : 'Play'}
                          </button>
                          <button className="btn btn-delete" onClick={() => handleDeleteAudio(product)}>
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');

  useEffect(() => {
    const saved = localStorage.getItem('userName');
    if (saved) { setUserName(saved); setIsLoggedIn(true); }
  }, []);

  const handleLogin = (name) => { setUserName(name); setIsLoggedIn(true); };
  const handleLogout = () => { localStorage.removeItem('userName'); setUserName(''); setIsLoggedIn(false); };

  return (
    <div className="App">
      {!isLoggedIn
        ? <LoginPage onLogin={handleLogin} />
        : <MainPage userName={userName} onLogout={handleLogout} />
      }
    </div>
  );
}

export default App;
